import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardCheck, LayoutTemplate } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { FormStatusBadge } from '../components/FormsList/FormStatusBadge';
import { FormRowActionsMenu } from '../components/FormsList/FormRowActionsMenu';
import { TemplateRowActionsMenu } from '../components/Templates/TemplateRowActionsMenu';
import type { FormSummary, TemplateSummary } from '../types';

interface FormColumnsOptions {
  t: TFunction;
  orgId: string;
}

/** A row in the merged Forms + Templates list (`FormsListPage`) — the two resources are separate backend endpoints, joined client-side. */
export type MergedListItem =
  | { itemType: 'form'; item: FormSummary }
  | { itemType: 'template'; item: TemplateSummary };

export function getFormColumns({
  t,
  orgId,
}: FormColumnsOptions): ColumnDef<FormSummary, unknown>[] {
  return [
    {
      accessorKey: 'title',
      header: t('list.table.form'),
      meta: { cellClassName: 'flex-[2] min-w-0' },
      cell: ({ row }) => (
        <Link
          to={`/${orgId}/forms/${row.original.id}`}
          className="font-medium text-foreground hover:underline"
          data-testid={`forms-list-row-link-${row.original.id}`}
        >
          {row.original.title}
        </Link>
      ),
    },
    {
      accessorKey: 'projectId',
      header: t('list.table.scope'),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.projectId ?? t('list.table.scopeOrganization')}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: t('list.table.status'),
      meta: { cellClassName: 'flex-none w-32' },
      cell: ({ row }) => <FormStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'submissionCount',
      header: t('list.table.responses'),
      meta: { cellClassName: 'flex-none w-24' },
      cell: ({ row }) => <span className="text-sm">{row.original.submissionCount}</span>,
    },
    {
      accessorKey: 'updatedAt',
      header: t('list.table.modified'),
      meta: { cellClassName: 'flex-none w-32' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: undefined,
      meta: { cellClassName: 'flex-none w-12 flex justify-end' },
      cell: ({ row }) => <FormRowActionsMenu form={row.original} />,
    },
  ];
}

/** Merged Forms + Templates list — `FormsListPage`'s main table. */
export function getMergedListColumns({
  t,
  orgId,
}: FormColumnsOptions): ColumnDef<MergedListItem, unknown>[] {
  return [
    {
      id: 'title',
      accessorFn: (entry) => entry.item.title,
      header: t('list.table.form'),
      meta: { cellClassName: 'flex-[2] min-w-0' },
      cell: ({ row }) => {
        const entry = row.original;
        const isTemplate = entry.itemType === 'template';
        const href = isTemplate
          ? `/${orgId}/forms/templates/${entry.item.id}`
          : `/${orgId}/forms/${entry.item.id}`;
        return (
          <div className="flex min-w-0 items-center gap-2">
            {isTemplate ? (
              <LayoutTemplate className="h-4 w-4 shrink-0 text-purple-600" />
            ) : (
              <ClipboardCheck className="h-4 w-4 shrink-0 text-blue-600" />
            )}
            <Link
              to={href}
              className="truncate font-medium text-foreground hover:underline"
              data-testid={`forms-list-row-link-${entry.item.id}`}
            >
              {entry.item.title}
            </Link>
            {isTemplate && (
              <Badge variant="secondary" className="shrink-0">
                {t('templates.badge')}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: 'scope',
      accessorFn: (entry) => (entry.itemType === 'form' ? entry.item.projectId : null),
      header: t('list.table.scope'),
      cell: ({ row }) => {
        const projectId = row.original.itemType === 'form' ? row.original.item.projectId : null;
        return (
          <span className="text-sm text-muted-foreground">
            {projectId ?? t('list.table.scopeOrganization')}
          </span>
        );
      },
    },
    {
      id: 'status',
      accessorFn: (entry) => (entry.itemType === 'form' ? entry.item.status : null),
      header: t('list.table.status'),
      meta: { cellClassName: 'flex-none w-32' },
      cell: ({ row }) =>
        row.original.itemType === 'form' ? (
          <FormStatusBadge status={row.original.item.status} />
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'responses',
      accessorFn: (entry) => (entry.itemType === 'form' ? entry.item.submissionCount : null),
      header: t('list.table.responses'),
      meta: { cellClassName: 'flex-none w-24' },
      cell: ({ row }) =>
        row.original.itemType === 'form' ? (
          <span className="text-sm">{row.original.item.submissionCount}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'updatedAt',
      accessorFn: (entry) => entry.item.updatedAt,
      header: t('list.table.modified'),
      meta: { cellClassName: 'flex-none w-32' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.item.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: undefined,
      meta: { cellClassName: 'flex-none w-12 flex justify-end' },
      cell: ({ row }) =>
        row.original.itemType === 'form' ? (
          <FormRowActionsMenu form={row.original.item} />
        ) : (
          <TemplateRowActionsMenu template={row.original.item} />
        ),
    },
  ];
}

/**
 * Row target is the respond flow (`/forms/{id}/respond`), not the editor — this
 * table is for recipients answering a form, not owners managing it.
 */
export function getAssignedFormColumns({
  t,
  orgId,
}: FormColumnsOptions): ColumnDef<FormSummary, unknown>[] {
  return [
    {
      accessorKey: 'title',
      header: t('list.table.form'),
      meta: { cellClassName: 'flex-[2] min-w-0' },
      cell: ({ row }) => (
        <Link
          to={`/${orgId}/forms/${row.original.id}/respond`}
          className="font-medium text-foreground hover:underline"
          data-testid={`forms-assigned-row-link-${row.original.id}`}
        >
          {row.original.title}
        </Link>
      ),
    },
    {
      accessorKey: 'status',
      header: t('list.table.status'),
      meta: { cellClassName: 'flex-none w-32' },
      cell: ({ row }) => <FormStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'updatedAt',
      header: t('list.table.modified'),
      meta: { cellClassName: 'flex-none w-32' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: undefined,
      meta: { cellClassName: 'flex-none w-12 flex justify-end' },
      cell: ({ row }) => {
        const canRespond = row.original.status === 'Shared';
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild={canRespond}
                variant="ghost"
                size="icon"
                disabled={!canRespond}
                aria-label={t('assignedToMe.respondButton')}
                data-testid={`forms-assigned-respond-${row.original.id}`}
              >
                {canRespond ? (
                  <Link to={`/${orgId}/forms/${row.original.id}/respond`}>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('assignedToMe.respondButton')}</TooltipContent>
          </Tooltip>
        );
      },
    },
  ];
}
